/**
 * Algorithm 7: Session Start & Resume
 * Initialize study session and show appropriate content
 */

import { applyScoreDecay } from './scoreDecay.js';

/**
 * Start study session with decay calculation
 * 
 * @param {Object} userProgress - User's progress data
 * @returns {Object} - Resume state with section and action
 */
export function startStudySession(userProgress) {
  // Step 1: Apply decay to all sections
  userProgress.sections = userProgress.sections.map(section =>
    applyScoreDecay(section, userProgress.sections)
  );

  // Step 2: Find last studied section
  const lastSectionId = userProgress.lastStudiedSection;
  const resumeSection = userProgress.sections.find(s => s.sectionId === lastSectionId);

  if (!resumeSection) {
    // Default to first section if none found
    const firstSection = userProgress.sections[0];
    return {
      action: 'SHOW_CONTENT',
      section: firstSection,
      message: `Welcome! Let's start with ${firstSection.title}`,
      nextAction: 'BEGIN_QUESTIONS',
    };
  }

  // Step 3: Return resume state
  return {
    action: 'SHOW_CONTENT',
    section: resumeSection,
    message: `Welcome back! Let's review ${resumeSection.title}`,
    nextAction: 'BEGIN_QUESTIONS',
  };
}

/**
 * Select warm-up question after content review
 * 
 * @param {Object} resumeSection - Section being resumed
 * @param {Array} allQuestions - All question progress data
 * @returns {Object} - Warm-up question selection
 */
export function beginQuestionsAfterResume(resumeSection, allQuestions) {
  // Select warm-up question from resume section
  // Prefer recently seen questions they got right (confidence builder)
  const recentQuestions = allQuestions
    .filter(q => q.sectionId === resumeSection.id && q.timesShown > 0)
    .sort((a, b) => b.lastShown - a.lastShown) // Most recent first
    .slice(0, 5); // Top 5 recent

  // Filter for ones they got right
  const correctQuestions = recentQuestions.filter(
    q => q.timesCorrect > q.timesIncorrect
  );

  // Pick one
  const warmUpQuestion =
    correctQuestions.length > 0 ? correctQuestions[0] : recentQuestions[0];

  if (!warmUpQuestion) {
    // No history - pick any question from section
    const sectionQuestions = allQuestions.filter(q => q.sectionId === resumeSection.id);
    return {
      question: sectionQuestions[0],
      reason: 'resume',
      isWarmUp: true,
    };
  }

  return {
    question: warmUpQuestion,
    reason: 'resume',
    isWarmUp: true,
  };
}
